import React, { useState } from "react";
import { X, Upload, CheckCircle, AlertCircle } from "lucide-react";
import apiService from "../services/api";
import { useTheme } from "../contexts/ThemeContext";

const LushaImportModal = ({ onClose, onSuccess }) => {
  const { isDarkMode } = useTheme();
  const [importing, setImporting] = useState(false);
  const [maxPages, setMaxPages] = useState(5);
  const [result, setResult] = useState(null);

  const handleImport = async () => {
    try {
      setImporting(true);
      setResult(null);

      const response = await apiService.importFromLusha(maxPages);

      if (response.success) {
        setResult(response);
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 2000);
      } else {
        setResult({
          success: false,
          message: response.message || "Import failed",
        });
      }
    } catch (error) {
      console.error("Import error:", error);
      setResult({
        success: false,
        message: error.response?.data?.message || "Failed to import from Lusha",
      });
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div
        className={`border rounded-xl max-w-md w-full ${
          isDarkMode
            ? "bg-[#0C0E12] border-gray-700"
            : "bg-white border-gray-300"
        }`}
      >
        {/* Header */}
        <div
          className={`flex items-center justify-between p-6 border-b ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <h2
            className={`text-lg font-semibold ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Import from Lusha
          </h2>
          <button
            onClick={onClose}
            className={`transition-colors ${
              isDarkMode
                ? "text-gray-400 hover:text-white"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {!result ? (
            <>
              <div className="space-y-4">
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Number of pages to import
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="20"
                    value={maxPages}
                    onChange={(e) => setMaxPages(parseInt(e.target.value))}
                    disabled={importing}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-[#13161B] border-gray-700 text-white focus:border-gray-600"
                        : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                    }`}
                  />
                  <p
                    className={`text-xs mt-2 ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Each page contains up to 50 contacts. Max 20 pages (1000
                    contacts).
                  </p>
                </div>

                <div
                  className={`rounded-lg p-4 ${
                    isDarkMode
                      ? "bg-blue-500/10 border border-blue-500/20"
                      : "bg-blue-50 border border-blue-200"
                  }`}
                >
                  <p
                    className={`text-sm ${
                      isDarkMode ? "text-blue-400" : "text-blue-700"
                    }`}
                  >
                    This will import all your existing Lusha contacts. Existing
                    leads will be updated with new information.
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  disabled={importing}
                  className={`flex-1 px-4 py-2 rounded-lg transition-colors disabled:opacity-50 ${
                    isDarkMode
                      ? "bg-[#13161B] border border-gray-700 text-gray-300 hover:bg-gray-700"
                      : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleImport}
                  disabled={importing}
                  className={`flex-1 px-4 py-2 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2 ${
                    isDarkMode
                      ? "bg-blue-600 hover:bg-blue-700 text-white"
                      : "bg-blue-500 hover:bg-blue-600 text-white"
                  }`}
                >
                  {importing ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Importing...
                    </>
                  ) : (
                    <>
                      <Upload size={16} />
                      Start Import
                    </>
                  )}
                </button>
              </div>
            </>
          ) : (
            <div className="space-y-4">
              {result.success ? (
                <>
                  <div className="flex items-center gap-3 text-green-500">
                    <CheckCircle size={24} />
                    <span className="font-medium">Import Successful!</span>
                  </div>

                  <div
                    className={`rounded-lg p-4 space-y-2 ${
                      isDarkMode ? "bg-[#13161B]" : "bg-gray-50"
                    }`}
                  >
                    <div className="flex justify-between text-sm">
                      <span
                        className={
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }
                      >
                        Total Contacts:
                      </span>
                      <span
                        className={`font-medium ${
                          isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        {result.total}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span
                        className={
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }
                      >
                        Imported (New):
                      </span>
                      <span className="text-green-500 font-medium">
                        {result.imported}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span
                        className={
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }
                      >
                        Updated (Existing):
                      </span>
                      <span className="text-blue-500 font-medium">
                        {result.updated}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span
                        className={
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }
                      >
                        Skipped:
                      </span>
                      <span
                        className={`font-medium ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        {result.skipped}
                      </span>
                    </div>
                    {result.errors?.length > 0 && (
                      <div className="flex justify-between text-sm">
                        <span
                          className={
                            isDarkMode ? "text-gray-400" : "text-gray-500"
                          }
                        >
                          Errors:
                        </span>
                        <span className="text-red-500 font-medium">
                          {result.errors.length}
                        </span>
                      </div>
                    )}
                  </div>

                  {result.errors?.length > 0 && (
                    <div
                      className={`rounded-lg p-4 ${
                        isDarkMode
                          ? "bg-red-500/10 border border-red-500/20"
                          : "bg-red-50 border border-red-200"
                      }`}
                    >
                      <p
                        className={`text-sm font-medium mb-2 ${
                          isDarkMode ? "text-red-400" : "text-red-600"
                        }`}
                      >
                        {result.errors.length} contacts had errors:
                      </p>
                      <div className="max-h-32 overflow-y-auto space-y-1">
                        {result.errors.slice(0, 5).map((err, idx) => (
                          <p
                            key={idx}
                            className={`text-xs ${
                              isDarkMode ? "text-red-300" : "text-red-500"
                            }`}
                          >
                            • {err.email}: {err.error}
                          </p>
                        ))}
                        {result.errors.length > 5 && (
                          <p
                            className={`text-xs ${
                              isDarkMode ? "text-red-300" : "text-red-500"
                            }`}
                          >
                            ... and {result.errors.length - 5} more
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="flex items-center gap-3 text-red-500">
                    <AlertCircle size={24} />
                    <span className="font-medium">Import Failed</span>
                  </div>
                  <div
                    className={`rounded-lg p-4 ${
                      isDarkMode
                        ? "bg-red-500/10 border border-red-500/20"
                        : "bg-red-50 border border-red-200"
                    }`}
                  >
                    <p
                      className={`text-sm ${
                        isDarkMode ? "text-red-300" : "text-red-600"
                      }`}
                    >
                      {result.message}
                    </p>
                  </div>
                  <button
                    onClick={() => setResult(null)}
                    className={`w-full px-4 py-2 rounded-lg transition-colors ${
                      isDarkMode
                        ? "bg-blue-600 hover:bg-blue-700 text-white"
                        : "bg-blue-500 hover:bg-blue-600 text-white"
                    }`}
                  >
                    Try Again
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LushaImportModal;
